import type {MaskOptsInterface} from '@Libraries/mask';
import {Mask} from '@Libraries/mask';
import {validateLineHeight} from '@Libraries/text-library';
import {
  addFonts,
  FONT_LOAD_STATUS,
  FONT_VARIATION,
  fontsRequestedMap,
  getFontFamilyNameForVariations,
  getFontFamilyString,
  getRegularFont,
  initFontsMap,
  isBoldVariationAvaliableForFont,
  isItalicVariationAvaliableForFont,
} from '@Libraries/font-library';
import {loadImageAsync} from '@Utils/image.util';
import type {MaskingTextObject, MaskingTextStyles} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {MIN_LETTER_SPACING} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import {setShowRemoveMask} from '@Panels/masking-panel/masking-panel-reducer';
import {setIsTextMaskEditingPanelDisabled, setTextMaskEditingOptions} from '@Panels/masking-panel/components/text-masking-panel/text-masking-panel-reducer';
import type {FabricTextItemStyles} from '@PosterWhiteboard/classes/text-styles.class';
import {BOLD_STROKE_WIDTH_FACTOR, DEFAULT_FONT_STYLE, DEFAULT_STROKE_WIDTH, TextHorizontalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {FabricImage, Pattern, Textbox} from '@postermywall/fabricjs-2';

const DEFAULT_FONT_FAMILY = 'ArchivoBlackRegular';
const DEFAULT_CHAR_SPACING = 0;
const DEFAULT_LINE_HEIGHT = 120;
const DEFAULT_TEXT_ALIGN = TextHorizontalAlignType.LEFT;

export class TextMask extends Mask {
  public maskingText?: Textbox;
  public transparentFillPattern?: Pattern;
  public maskingTextStyle?: MaskingTextStyles;

  protected keyboardShortcutScope = KeyboardShortcutScope.ITEM_TEXT_MASKING;
  declare initialMaskingData?: MaskingTextObject;

  constructor(id: string, url: string, parentSelector: string, opts: MaskOptsInterface = {}) {
    super(id, url, parentSelector, opts);
  }

  private initLocalVariables(): void {
    this.maskingTextStyle = this.initialMaskingData?.textStyles ?? this.getDefaultMaskingStyles();
  }

  public getDefaultMaskingStyles = (): MaskingTextStyles => {
    return {
      fontSize: this.getTextItemDefaultFont(),
      letterSpacing: DEFAULT_CHAR_SPACING,
      isItalic: false,
      isBold: false,
      lineThrough: false,
      underLine: false,
      textAlign: TextHorizontalAlignType.LEFT,
      leading: DEFAULT_LINE_HEIGHT,
      fontFamily: DEFAULT_FONT_FAMILY,
    };
  };

  public async addInitialTextItem(): Promise<void> {
    await this.loadTransparentFillPattern();
    await this.restoreMask();
  }

  public setTextProperty(property: string, value: any): void {
    if (this.maskingText) {
      this.maskingText.set(property, value);
      this.invalidate();
    }
  }

  public setFontFamily(fontFamily: string): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {
        ...this.maskingTextStyle,
        fontFamily,
      };
      this.invalidate();
    }
  }

  public setFontSize(fontSize: number): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, fontSize};
      this.invalidate();
    }
  }

  public setAlignment(textAlign: TextHorizontalAlignType): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, textAlign};
      this.invalidate();
    }
  }

  public setLeading(leading: number): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, leading};
      this.invalidate();
    }
  }

  public setLetterSpacing(letterSpacing: number): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, letterSpacing};
      this.invalidate();
    }
  }

  public setIsBold(isBold: boolean): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, isBold};
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({isBold}));
      this.invalidate();
    }
  }

  public setIsItalic(isItalic: boolean): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, isItalic};
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({isItalic}));
      this.invalidate();
    }
  }

  public setUnderline(underLine: boolean): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, underLine};
      this.invalidate();
    }
  }

  public setLineThrough(lineThrough: boolean): void {
    if (this.maskingTextStyle) {
      this.maskingTextStyle = {...this.maskingTextStyle, lineThrough};
      this.invalidate();
    }
  }

  public async init(): Promise<void> {
    await super.init();
    await this.onInit();
  }

  public async onInit(): Promise<void> {
    if (this.canvas.backgroundImage) {
      this.canvas.backgroundImage.opacity = 1;
    }
    this.canvas.on('object:added', this.onItemAdded.bind(this));
    this.canvas.on({
      'object:moving': this.updatePatternFill.bind(this),
      'object:scaling': this.updatePatternFill.bind(this),
      'object:resizing': this.updatePatternFill.bind(this),
      'object:rotating': this.updatePatternFill.bind(this),
      'object:skewing': this.updatePatternFill.bind(this),
    });
    this.initLocalVariables();
    await this.addInitialTextItem();
    this.updateReduxStates();
  }

  private updateReduxStates(): void {
    if (this.maskingTextStyle) {
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions(this.maskingTextStyle));
    }
  }

  private getTextItemDefaultWidth(): number {
    const canvasNonScaledWidth = this.canvas.width / this.scale;
    const TEXT_WIDTH_RATIO_TO_CANVAS = 0.85;
    return canvasNonScaledWidth * TEXT_WIDTH_RATIO_TO_CANVAS;
  }

  private getTextItemDefaultFont(): number {
    const canvasNonScaledWidth = this.canvas.width / this.scale;
    const TEXT_FONT_RATIO_TO_CANVAS = 0.1125;
    return canvasNonScaledWidth * TEXT_FONT_RATIO_TO_CANVAS;
  }

  public removeMasking(): void {
    if (this.maskingText) {
      this.canvas.remove(this.maskingText);
      this.maskingText = undefined;
      this.invalidate();
    }
  }

  public isMaskingAppiled(): boolean {
    return !!this.maskingText;
  }

  public updatePatternFill(): void {
    if (this.maskingText && this.transparentFillPattern) {
      const cornerPoints = this.maskingText.getCoords();
      this.transparentFillPattern.offsetX = -cornerPoints[0].x;
      this.transparentFillPattern.offsetY = -cornerPoints[0].y;
      this.invalidate();
    }
  }

  public invalidate(): void {
    super.invalidate();

    const currentRemoveMaskState = window.PMW.redux.store.getState().masking.showRemoveMask;
    if (currentRemoveMaskState !== this.isMaskingAppiled()) {
      window.PMW.redux.store.dispatch(setShowRemoveMask(this.isMaskingAppiled()));
    }

    const currentEditingPanelDisabledState = window.PMW.redux.store.getState().textMasking.isEditingPanelDisabled;
    if (currentEditingPanelDisabledState !== !this.isMaskingAppiled()) {
      window.PMW.redux.store.dispatch(setIsTextMaskEditingPanelDisabled(!this.isMaskingAppiled()));
    }

    if (this.maskingText?.fontSize !== this.maskingTextStyle?.fontSize) {
      this.maskingText?.set('fontSize', this.maskingTextStyle?.fontSize);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({fontSize: this.maskingTextStyle?.fontSize}));
    }

    if (this.maskingText?.textAlign !== this.maskingTextStyle?.textAlign) {
      this.maskingText?.set('textAlign', this.maskingTextStyle?.textAlign);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({textAlign: this.maskingTextStyle?.textAlign}));
    }

    if (this.maskingText?.lineHeight !== this.maskingTextStyle?.leading) {
      const validatedLineHeight = validateLineHeight(this.maskingTextStyle?.leading ?? 10);
      this.maskingText?.set('lineHeight', validatedLineHeight);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({leading: this.maskingTextStyle?.leading}));
    }

    if (this.maskingText?.charSpacing !== this.maskingTextStyle?.letterSpacing) {
      this.maskingText?.set('charSpacing', this.maskingTextStyle?.letterSpacing);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({letterSpacing: this.maskingTextStyle?.letterSpacing}));
    }

    if (this.maskingText?.underline !== this.maskingTextStyle?.underLine) {
      this.maskingText?.set('underline', this.maskingTextStyle?.underLine);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({underLine: this.maskingTextStyle?.underLine}));
    }

    if (this.maskingText?.linethrough !== this.maskingTextStyle?.lineThrough) {
      this.maskingText?.set('linethrough', this.maskingTextStyle?.lineThrough);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({lineThrough: this.maskingTextStyle?.lineThrough}));
    }

    if (this.maskingText?.fontFamily !== this.maskingTextStyle?.fontFamily) {
      this.maskingText?.set('fontFamily', this.maskingTextStyle?.fontFamily);
      window.PMW.redux.store.dispatch(setTextMaskEditingOptions({fontFamily: this.maskingTextStyle?.fontFamily}));
    }

    if (this.maskingText) {
      this.maskingText.set(this.getFontStyles());
    }
  }

  private getFontStyles(): Partial<FabricTextItemStyles> {
    const font = getRegularFont(this.maskingTextStyle?.fontFamily ?? '');
    const fontFamilyWithVariation = getFontFamilyNameForVariations(
      this.maskingTextStyle?.fontFamily ?? '',
      this.maskingTextStyle?.isBold ?? false,
      this.maskingTextStyle?.isItalic ?? false
    );
    const isFontLoaded = fontsRequestedMap[fontFamilyWithVariation] === FONT_LOAD_STATUS.LOADED;
    const isBoldApplied = isFontLoaded ? isBoldVariationAvaliableForFont(font) : false;
    const isItalicApplied = isFontLoaded ? isItalicVariationAvaliableForFont(font) : false;

    const styles: Partial<FabricTextItemStyles> = {
      fontSize: this.maskingTextStyle?.fontSize ?? 20,
      fontFamily: getFontFamilyString(fontFamilyWithVariation),
      // fontWeight: this.fontWeight,
      // should be getting normal instead of italic for menu
      fontStyle: this.maskingTextStyle?.isItalic && !isItalicApplied ? FONT_VARIATION.ITALIC : DEFAULT_FONT_STYLE,
      // fontStyle: DEFAULT_FONT_STYLE,
      textAlign: this.maskingTextStyle?.textAlign ?? TextHorizontalAlignType.LEFT,
      // fill: this.fill.getFill(fillWidth, fillHeight),
      charSpacing: this.maskingTextStyle?.letterSpacing ?? MIN_LETTER_SPACING,
      linethrough: this.maskingTextStyle?.lineThrough ?? false,
      underline: this.maskingTextStyle?.underLine ?? false,
      lineHeight: validateLineHeight(this.maskingTextStyle?.leading ?? 120),
      stroke: null,
      strokeLineJoin: 'miter',
      strokeWidth: DEFAULT_STROKE_WIDTH,
    };

    if (this.maskingTextStyle?.isBold && !isBoldApplied && this.maskingText) {
      return {
        ...styles,
        stroke: this.maskingText.getValueOfPropertyAt(0, 0, 'fill') as string,
        strokeLineJoin: 'round',
        strokeWidth: this.maskingTextStyle.fontSize * BOLD_STROKE_WIDTH_FACTOR,
      };
    }

    return styles;
  }

  // TODO: this could be static
  public getTextAlign(textAlign: string): TextHorizontalAlignType {
    switch (textAlign) {
      case 'left':
        return TextHorizontalAlignType.LEFT;
      case 'right':
        return TextHorizontalAlignType.RIGHT;
      case 'center':
        return TextHorizontalAlignType.CENTER;
      case 'justify':
        return TextHorizontalAlignType.JUSTIFY;
      default:
        throw new Error('Unhandled text align string');
    }
  }

  public async loadTransparentFillPattern(): Promise<void> {
    const img = await loadImageAsync(window.PMW.util.asset_url('images/postermaker/transparent-pattern.jpg'));
    this.transparentFillPattern = new Pattern({
      source: img,
      repeat: 'repeat',
    });
  }

  public restoreMask(): Promise<void> {
    const opts: Record<string, any> = {};
    let text = window.i18next.t('pmwjs_add_text');
    let font = DEFAULT_FONT_FAMILY;

    opts.hasControls = true;
    opts.editingBorderColor = 'rgba(63,188,231,0.5)';
    opts.selectionColor = 'rgba(62,164,204,0.3)';
    opts.cursorColor = '#1A4556';
    opts.cursorDelay = 1000;
    opts.lockSkewingX = true;
    opts.lockSkewingY = true;
    opts.cursorDuration = 600;
    opts.perPixelTargetFind = false;

    if (this.initialMaskingData) {
      opts.angle = this.initialMaskingData.angle;
      opts.width = this.initialMaskingData.width;
      opts.left = this.initialMaskingData.left * this.getScaleX();
      opts.top = this.initialMaskingData.top * this.getScaleY();
      opts.scaleX = this.initialMaskingData.scaleX * this.getScaleX();
      opts.scaleY = this.initialMaskingData.scaleY * this.getScaleY();
      opts.fontSize = this.initialMaskingData.textStyles.fontSize;
      opts.charSpacing = this.initialMaskingData.textStyles.letterSpacing;
      opts.lineHeight = validateLineHeight(this.initialMaskingData.textStyles.leading);
      opts.textAlign = this.initialMaskingData.textStyles.textAlign;
      opts.underline = this.initialMaskingData.textStyles.underLine;
      opts.linethrough = this.initialMaskingData.textStyles.lineThrough;
      opts.isBold = this.initialMaskingData.textStyles.isBold;
      opts.isItalic = this.initialMaskingData.textStyles.isItalic;
      text = this.initialMaskingData.text;
      font = getFontFamilyNameForVariations(this.initialMaskingData.textStyles.fontFamily, this.initialMaskingData.textStyles.isBold, this.initialMaskingData.textStyles.isItalic);
    } else {
      const defaultScale = 1;
      const defaultHeight = 90;
      const textItemDefaultWidth = this.getTextItemDefaultWidth();
      const textItemDefaultFont = this.getTextItemDefaultFont();

      opts.width = textItemDefaultWidth;
      opts.fontSize = textItemDefaultFont;
      opts.scaleX = defaultScale;
      opts.scaleY = defaultScale;
      opts.charSpacing = DEFAULT_CHAR_SPACING;
      opts.lineHeight = validateLineHeight(DEFAULT_LINE_HEIGHT);
      opts.textAlign = DEFAULT_TEXT_ALIGN;
      opts.left = (this.canvas.getWidth() / this.scale - textItemDefaultWidth) / 2;
      opts.top = (this.canvas.getHeight() / this.scale - defaultHeight) / 2;
      opts.underline = false;
      opts.linethrough = false;
      opts.isBold = false;
      opts.isItalic = false;
    }

    return new Promise<void>((resolve, reject) => {
      addFonts(
        [font],
        () => {
          opts.fontFamily = font;
          this.canvas.add(new Textbox(text, opts));
          resolve();
        },
        reject
      );
    });
  }

  public async onItemAdded(e: CanvasEvents['object:added']): Promise<void> {
    const viewComponent = e.target;
    if (viewComponent instanceof FabricImage) {
      return;
    }

    if (!viewComponent) {
      return;
    }
    viewComponent.set('fill', this.transparentFillPattern);
    viewComponent.setControlsVisibility({
      tl: true,
      tr: true,
      br: true,
      bl: true,
      ml: true,
      mr: true,
      mtr: true,
    });

    this.maskingText = viewComponent as Textbox;
    this.maskingText.setCoords();
    this.canvas.discardActiveObject();
    this.canvas.setActiveObject(this.maskingText);
    this.invalidate();
  }
}

export const createTextMask = async (id: string, url: string, parentSelector: string, opts: MaskOptsInterface = {}): Promise<TextMask> => {
  await initFontsMap();
  const textMask = new TextMask(id, url, parentSelector, opts);
  await textMask.init();
  return textMask;
};
